/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';

import { i18n } from './messages';

const styles = cssMap({
	gradientBorderWrapper: {
		display: 'inline-flex',
		borderRadius: token('radius.small'),
		// @ts-expect-error - 1px is not in the ADS tokens but the design requires it
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '1px',
		backgroundImage:
			'conic-gradient(from 45deg, #FCA700 0% 12%, #94C748 17% 45%, #0065FF 50% 62%, #C97CF4 67% 100%)',
	},
	customSummarizeButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		height: '22px', // +2px border from wrapper = 24px (compact button height)
		paddingBlock: token('space.025'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		borderStyle: 'none',
		backgroundColor: token('elevation.surface'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
		'&:focus-visible': {
			outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

export type SummarizeInChatProps = {
	url: string | undefined;
	testId?: string;
	onClick?: () => void;
	showGradientBorder?: boolean;
	/**
	 * Spacing for the underlying Atlaskit Button when the gradient border is
	 * NOT shown. Defaults to `'compact'` to preserve existing per-row hover
	 * behavior. Pass `'default'` to render the standard 32px-tall secondary
	 * button (e.g. when sitting alongside the Related links footer toggle).
	 */
	spacing?: 'compact' | 'default';
};

export const SummarizeInChatButton = ({
	url,
	testId,
	onClick,
	showGradientBorder = false,
	spacing = 'compact',
}: SummarizeInChatProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	const promptRovoChat = (e: React.MouseEvent<HTMLButtonElement>) => {
		onClick?.();
		e.stopPropagation();

		if (!url) {
			return;
		}

		publish({
			type: 'browser-context-payload',
			source: 'search-result',
			data: {
				context: {
					browserUrl: url,
				},
			},
		});
		publish({
			type: 'chat-new',
			source: 'search-result',
			data: {
				dialogues: [],
				prompt: `Summarize ${url}`,
				name: `Summarize ${url}`,
			},
		});
	};

	if (showGradientBorder) {
		return (
			<Box as="span" xcss={styles.gradientBorderWrapper} testId="summarize-in-chat-gradient-border">
				<Pressable xcss={styles.customSummarizeButton} onClick={promptRovoChat} testId={testId}>
					<RovoChatIcon label="" />
					{formatMessage(i18n.summarizeInChatButtonText)}
				</Pressable>
			</Box>
		);
	}

	return (
		<Button iconBefore={RovoChatIcon} spacing={spacing} onClick={promptRovoChat} testId={testId}>
			{formatMessage(i18n.summarizeInChatButtonText)}
		</Button>
	);
};
